import { useState } from "react";
import { Activity, Tv, Bike, BookOpen, ChevronDown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SYSTEMS = [
  {
    title: "Racing Intelligence",
    icon: Activity,
    colour: "#F97316",
    sentence: "Live scoring of every runner's true performance — efficiency, pressure, momentum, and ground lost — distilled into one-sentence verdicts and next-start signals.",
    path: "/"
  },
  {
    title: "Broadcast Overlay",
    icon: Tv,
    colour: "#EF4444",
    sentence: "Television-grade graphics layer with real-time callouts, runner tags, and AI commentary — compositable directly into live broadcast video.",
    path: "/broadcast"
  },
  {
    title: "Cycling Vision",
    icon: Bike,
    colour: "#A855F7",
    sentence: "Same engine, different sport. Peloton pressure, breakaway strength, and chase momentum — proving the universal spine works beyond racing.",
    path: "/cross-sport"
  },
  {
    title: "Race Library",
    icon: BookOpen,
    colour: "#F59E0B",
    sentence: "Every race stored as a searchable intelligence case. Why the race was won, replay timelines, and individual runner stories — knowledge that compounds.",
    path: "/library"
  }
];

const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setIsLoading(true);
    await onLogin(username, password);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#090a0d] flex flex-col items-center justify-center p-6" data-testid="login-page">
      {/* Branding */}
      <div className="text-center mb-10">
        <div className="text-[#F97316] text-xs tracking-[0.4em] font-bold uppercase mb-2">RACE INTELLIGENCE LIVE</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white leading-tight">UK Pilot v2</h1>
        <p className="text-white/30 text-sm mt-2 max-w-md mx-auto">
          One engine, any sport.
        </p>
      </div>

      {/* 3-Box Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl w-full mb-10" data-testid="system-overview">
        {SYSTEMS.map((sys) => {
          const Icon = sys.icon;
          return (
            <div
              key={sys.title}
              className="p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-all group"
              data-testid={`system-card-${sys.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${sys.colour}15` }}>
                  <Icon className="w-4.5 h-4.5" style={{ color: sys.colour }} />
                </div>
                <h3 className="text-white font-bold text-sm">{sys.title}</h3>
              </div>
              <p className="text-white/40 text-xs leading-relaxed">{sys.sentence}</p>
            </div>
          );
        })}
      </div>

      {/* Login Section */}
      <div className="w-full max-w-sm">
        {!showLogin ? (
          <div className="text-center">
            <Button
              onClick={() => setShowLogin(true)}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-8 h-11"
              data-testid="show-login-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Sign In to Access
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 animate-fade-in">
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="bg-[#121212] border-white/20 text-white placeholder:text-white/30 h-10"
              data-testid="username-input"
              autoFocus
            />
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="bg-[#121212] border-white/20 text-white placeholder:text-white/30 h-10"
              data-testid="password-input"
            />
            <Button
              type="submit"
              disabled={isLoading || !username || !password}
              className="w-full h-10 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold"
              data-testid="login-submit-btn"
            >
              {isLoading ? "Authenticating..." : "Sign In"}
            </Button>
          </form>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-white/10 text-xs">Confidential · All access logged and watermarked</p>
      </div>
    </div>
  );
};

export default LoginPage;
